import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import useViewTracker from "../../hooks/useViewTracker";
import HeroCarousel from "../../components/HeroCarousel";
import { api_get } from "../../lib/api";
import { useTranslation } from "react-i18next";
import {
    ArrowUpRight,
    Star,
    MapPin,
    Car,
    Map as MapIcon,
    PlaneTakeoff,
    ShieldCheck,
    Phone,
    Sparkles,
    Quote,
    Languages,
    Utensils,
    Plane,
    Globe2,
    CheckCircle2,
    HeartHandshake,
} from "lucide-react";
import { useData } from "../../context/DataContext";
import { TESTIMONIALS } from "../../data/seedData";
import { formatCurrency } from "../../lib/format";
import SEO from "../../components/SEO";
import FAQ from "../../components/FAQ";
import SmartImage from "../../components/SmartImage";
import ReviewsCarousel from "../../components/ReviewsCarousel";
import enTrans from "../../i18n/locales/en.json";

/* Hero carousel slides — Kerala-themed AI-generated photos stored in GridFS.
   The list is fetched from /api/hero-images at runtime so each environment
   (preview / production) serves its own GridFS files. */

/* helpful: pull rich arrays from EN as fallback if active locale doesn't define them */
const useFallbackArray = (key) => {
    const { t, i18n } = useTranslation();
    const local = t(key, { returnObjects: true });
    if (Array.isArray(local) && local.length) return local;
    const segs = key.split(".").reduce((o, s) => o?.[s], enTrans);
    return Array.isArray(segs) ? segs : [];
};

const PreviewHome = () => {
    const { packages, popularDestinations } = useData();
    const { t } = useTranslation();
    useViewTracker("page", "/");

    const [heroSlides, setHeroSlides] = useState([]);
    useEffect(() => {
        let cancelled = false;
        api_get("/hero-images")
            .then((data) => {
                if (cancelled || !Array.isArray(data)) return;
                setHeroSlides(
                    data.map((s) => ({
                        slot: s.label || s.slot,
                        src: `${process.env.REACT_APP_BACKEND_URL}${s.url}`,
                        alt: s.alt,
                    }))
                );
            })
            .catch(() => { /* silent — falls back to static SVG below */ });
        return () => { cancelled = true; };
    }, []);
    const [googleReviews, setGoogleReviews] = useState(null);
    useEffect(() => {
        let cancelled = false;
        api_get("/reviews")
            .then((data) => { if (!cancelled && data && Array.isArray(data.reviews)) setGoogleReviews(data); })
            .catch(() => { /* silent — le badge garde sa valeur par défaut */ });
        return () => { cancelled = true; };
    }, []);
    const featured = useMemo(
        () => (packages || []).filter((p) => p.active).slice(0, 5),
        [packages],
    );

    const vehicleRows = useFallbackArray("vehicleTable.rows");
    const faqItems = useFallbackArray("faq.items");

    /* JSON-LD TravelAgency for the homepage (per user spec) */
    const travelAgencyJsonLd = {
        "@context": "https://schema.org",
        "@type": "TravelAgency",
        name: "SouthTrip.in by LuxSaCabs",
        url: "https://southtrip.in",
        description:
            "Kerala cab booking, airport transfers and curated tour packages with verified drivers, transparent fares and 24/7 support.",
        areaServed: { "@type": "Place", name: "Kerala, India" },
        brand: { "@type": "Brand", name: "LuxSaCabs" },
        serviceType: [
            "Cab Booking",
            "Airport Transfers",
            "Kerala Tour Packages",
            "Private Driver Service",
        ],
        sameAs: ["https://www.instagram.com/southtrip.in"],
    };

    /* JSON-LD Review schema — uniquement à partir des avis Google réellement affichés (carrousel) */
    const reviewJsonLd = googleReviews && googleReviews.reviews.length ? {
        "@context": "https://schema.org",
        "@type": "Product",
        name: "SouthTrip Kerala cab & tour service",
        image: "https://southtrip.in/og-cover-1200x630.jpg",
        description:
            "Premium Kerala cab service, airport transfers and tour packages with Hindi & Arabic-speaking drivers.",
        brand: { "@type": "Brand", name: "SouthTrip.in by LuxSaCabs" },
        aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: String(googleReviews.rating),
            reviewCount: String(googleReviews.totalReviews),
            bestRating: "5",
            worstRating: "1",
        },
        review: googleReviews.reviews.map((r) => ({
            "@type": "Review",
            author: { "@type": "Person", name: r.authorName },
            reviewRating: {
                "@type": "Rating",
                ratingValue: String(r.rating || 5),
                bestRating: "5",
            },
            reviewBody: r.text,
        })),
    } : null;

    /* Visible homepage FAQ — also drives FAQPage JSON-LD via the FAQ component */
    const HOMEPAGE_FAQS = [
        {
            q: "How much does a cab from Cochin to Munnar cost?",
            a: "A private cab from Cochin to Munnar starts from ₹2,400/day for a sedan (Toyota Etios/Dzire, 4 passengers) including driver and fuel. SUV Innova Crysta starts at ₹3,500/day for larger groups.",
        },
        {
            q: "Do you offer airport pickup from Cochin International Airport?",
            a: "Yes, we offer 24/7 airport transfer services from Cochin (COK), Trivandrum (TRV), and Calicut (CCJ) airports to all major Kerala destinations. Our drivers track your flight in real time.",
        },
        {
            q: "Do you have Hindi-speaking drivers for North India visitors?",
            a: "Yes. All our drivers for North India visitors speak Hindi and English. We handle 600+ North India bookings every season and provide GST receipts and pre-paid invoices.",
        },
        {
            q: "Is halal food available on Kerala tour packages?",
            a: "Yes. We pre-book halal-certified restaurants along all routes including Cochin, Munnar, Alleppey and Thekkady. Mosque-stop maps and no-alcohol stays are available on request.",
        },
        {
            q: "Do I need to pay in advance to book a Kerala cab?",
            a: "No advance payment required. You pay directly to the driver via Cash, UPI, or card after your trip. No surge pricing. GST invoice sent by email after every booking.",
        },
    ];

    return (
        <div data-testid="preview-home">
            <SEO
                title={t("seo.homeTitle")}
                description={t("seo.homeDescription")}
                path="/"
                jsonLd={[travelAgencyJsonLd, reviewJsonLd].filter(Boolean)}
                keywords="kerala cab booking, kerala taxi service, cochin airport taxi, trivandrum airport transfer, calicut airport taxi, munnar cab booking, alleppey taxi, kerala tour packages with driver, private driver in kerala"
            />
            {/* HERO — asymmetric editorial layout */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-8 sm:pt-12 pb-14 sm:pb-20 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-end">
                <div className="lg:col-span-7 hz-rise order-2 lg:order-1">
                    <div className="hz-chip-brand">
                        <Sparkles className="w-3 h-3" /> {t("home.trustSafe")}
                    </div>
                    <h1 className="font-hz-display text-[40px] sm:text-5xl lg:text-[72px] leading-[0.95] tracking-tight mt-4 sm:mt-6">
                        {t("seo.homeH1")}
                    </h1>
                    <p className="text-base sm:text-lg text-[var(--ink-soft)] mt-4 sm:mt-6 max-w-xl leading-relaxed">
                        {t("home.heroSub")}
                    </p>
                    <div className="mt-6 sm:mt-10 flex flex-wrap items-center gap-3">
                        <Link to="/cab-booking" className="hz-btn-primary">
                            {t("services.cabBookingTitle")} <ArrowUpRight className="w-4 h-4" />
                        </Link>
                        <Link to="/tour-packages" className="hz-btn-ghost">
                            {t("services.packagesTitle")} <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>
                    <div className="mt-8 sm:mt-12 flex items-center gap-4 sm:gap-6 text-sm text-[var(--ink-soft)]">
                        <div className="flex -space-x-2">
                            {TESTIMONIALS.slice(0, 3).map((tm, i) => (
                                <img
                                    key={i}
                                    src={tm.avatar}
                                    alt={`${tm.name} — SouthTrip Kerala customer review`}
                                    className="w-9 h-9 rounded-full border-2 border-[var(--bg)] object-cover"
                                />
                            ))}
                        </div>
                        <div>
                            <div className="flex items-center gap-1 text-[var(--ink)] font-semibold">
                                <Star className="w-3.5 h-3.5 fill-[var(--ink)]" /> {googleReviews ? Number(googleReviews.rating).toFixed(1) : "4.9"}
                            </div>
                            <div className="text-xs">Google reviews · verified</div>
                        </div>
                    </div>
                </div>

                <div className="lg:col-span-5 relative hz-rise order-1 lg:order-2" style={{ animationDelay: "150ms" }}>
                    <div className="aspect-[4/5] sm:aspect-[3/4] rounded-[32px] overflow-hidden bg-[var(--bg-surface)] relative">
                        {heroSlides.length > 0 ? (
                            <HeroCarousel slides={heroSlides} intervalMs={6000} />
                        ) : (
                            <img
                                src="/default-kerala-destination.svg"
                                alt="Kerala destinations — SouthTrip"
                                className="w-full h-full object-cover"
                                fetchPriority="high"
                                loading="eager"
                                decoding="async"
                                width="1200"
                                height="1500"
                            />
                        )}
                    </div>
                    <div className="absolute -left-4 lg:-left-8 bottom-6 lg:bottom-8 hz-blur-card rounded-2xl p-4 lg:p-5 w-48 lg:w-56 hidden sm:block">
                        <div className="text-[10px] text-[var(--ink-softer)] uppercase tracking-widest">{t("packages.startingFrom")}</div>
                        <div className="font-hz-display text-3xl lg:text-4xl leading-none mt-1">₹1,900<span className="text-base font-normal text-[var(--ink-soft)]">/{t("common.days")}</span></div>
                        <div className="text-xs text-[var(--ink-soft)] mt-2">{t("cabs.driverIncluded")} · {t("cabs.fuelIncluded")}</div>
                    </div>
                    <div className="absolute -right-2 lg:-right-4 -top-2 lg:-top-4 hz-card p-3 lg:p-4 w-40 lg:w-44 hidden sm:block">
                        <div className="text-[10px] text-[var(--ink-softer)] uppercase tracking-widest">{t("common.from")} Cochin</div>
                        <div className="font-hz-display text-lg lg:text-xl mt-1 leading-tight">Munnar escape</div>
                        <div className="text-xs text-[var(--ink-soft)] mt-1">3 {t("common.days")} · ₹8,300</div>
                    </div>
                </div>
            </section>

            {/* ===== OUR SERVICES — 4 cards (Cab, Airport, Packages, Destinations) ===== */}
            <section
                className="max-w-7xl mx-auto px-6 pb-24"
                aria-labelledby="services-title"
                data-testid="services-section"
            >
                <div className="text-center max-w-3xl mx-auto mb-10">
                    <div className="hz-pill">{t("services.title")}</div>
                    <h2
                        id="services-title"
                        className="font-hz-display text-3xl md:text-5xl tracking-tight mt-4"
                    >
                        {t("services.title")}
                    </h2>
                    <p className="text-[var(--ink-soft)] mt-3 text-base">{t("services.sub")}</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {[
                        {
                            to: "/cab-booking",
                            title: t("services.cabBookingTitle"),
                            sub: t("services.cabBookingDesc"),
                            icon: Car,
                            tone: "bg-[var(--ink)] text-white",
                            testid: "service-cab-booking",
                        },
                        {
                            to: "/airport-transfers",
                            title: t("services.airportTitle"),
                            sub: t("services.airportDesc"),
                            icon: PlaneTakeoff,
                            tone: "bg-white text-[var(--ink)] border border-[var(--border)]",
                            testid: "service-airport-transfers",
                        },
                        {
                            to: "/tour-packages",
                            title: t("services.packagesTitle"),
                            sub: t("services.packagesDesc"),
                            icon: MapIcon,
                            tone: "bg-white text-[var(--ink)] border border-[var(--border)]",
                            testid: "service-tour-packages",
                        },
                        {
                            to: "/destinations",
                            title: t("services.destinationsTitle"),
                            sub: t("services.destinationsDesc"),
                            icon: MapPin,
                            tone: "bg-[var(--bg-surface)] text-[var(--ink)] border border-[var(--border)]",
                            testid: "service-destinations",
                        },
                    ].map((s) => (
                        <Link
                            key={s.title}
                            to={s.to}
                            className={`hz-card group p-7 flex flex-col justify-between min-h-[260px] ${s.tone}`}
                            style={{
                                background:
                                    s.tone.includes("bg-[var(--ink)]") ? "var(--ink)" : undefined,
                            }}
                            data-testid={s.testid}
                        >
                            <s.icon className="w-7 h-7 opacity-80" />
                            <div>
                                <h3 className="font-hz-display text-2xl md:text-3xl leading-none tracking-tight">
                                    {s.title}
                                </h3>
                                <div className="flex items-end justify-between gap-3 mt-3">
                                    <p
                                        className={`text-xs leading-relaxed ${
                                            s.tone.includes("bg-[var(--ink)]")
                                                ? "text-white/75"
                                                : "text-[var(--ink-soft)]"
                                        }`}
                                    >
                                        {s.sub}
                                    </p>
                                    <ArrowUpRight className="w-5 h-5 shrink-0 transition group-hover:rotate-45" />
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            {/* ===== TRUST GULF + NORTH INDIA — SEO content section ===== */}
            <section
                className="max-w-7xl mx-auto px-4 sm:px-6 pb-24"
                aria-labelledby="trust-gulf-title"
                data-testid="trust-gulf-section"
            >
                <div className="text-center max-w-3xl mx-auto mb-12">
                    <div className="hz-pill inline-flex items-center gap-2">
                        <Globe2 className="w-3 h-3" /> Gulf · North India · World
                    </div>
                    <h2
                        id="trust-gulf-title"
                        className="font-hz-display text-3xl md:text-5xl tracking-tight mt-4 leading-[1.05]"
                    >
                        {t("trustGulf.title")}
                    </h2>
                    <p className="text-[var(--ink-soft)] mt-4 text-base leading-relaxed">
                        {t("trustGulf.sub")}
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {[
                        {
                            icon: Languages,
                            title: t("trustGulf.hindiDriversTitle"),
                            sub: t("trustGulf.hindiDriversSub"),
                            cls: "bg-white border border-[var(--border)]",
                        },
                        {
                            icon: HeartHandshake,
                            title: "Family-friendly Kerala trips",
                            sub: "Comfortable cars, polite drivers and flexible stops for families with children or elders. Airport pickup, luggage help, hotel transfers and Kerala sightseeing made simple.",
                            cls: "!bg-[var(--ink)] text-white border-transparent",
                        },
                        {
                            icon: Utensils,
                            title: t("trustGulf.halalTitle"),
                            sub: t("trustGulf.halalSub"),
                            cls: "bg-white border border-[var(--border)]",
                        },
                        {
                            icon: Plane,
                            title: t("trustGulf.directFlightsTitle"),
                            sub: t("trustGulf.directFlightsSub"),
                            cls: "bg-white border border-[var(--border)]",
                        },
                        {
                            icon: ShieldCheck,
                            title: t("trustGulf.northIndiaTitle"),
                            sub: t("trustGulf.northIndiaSub"),
                            cls: "bg-[var(--bg-surface)] border border-[var(--border)]",
                        },
                        {
                            icon: CheckCircle2,
                            title: "Pay direct to driver",
                            sub: "Cash · UPI · card. No advance, no surge. Invoices and GST receipts on email after every booking.",
                            cls: "bg-white border border-[var(--border)]",
                        },
                    ].map((card, i) => (
                        <article
                            key={i}
                            className={`hz-card p-7 ${card.cls}`}
                            data-testid={`trust-card-${i}`}
                        >
                            <card.icon className="w-7 h-7 mb-4 opacity-85" />
                            <h3 className="font-hz-display text-xl md:text-2xl tracking-tight leading-tight">
                                {card.title}
                            </h3>
                            <p
                                className={`text-sm mt-3 leading-relaxed ${
                                    card.cls.includes("bg-[var(--ink)]")
                                        ? "text-white/75"
                                        : "text-[var(--ink-soft)]"
                                }`}
                            >
                                {card.sub}
                            </p>
                        </article>
                    ))}
                </div>
            </section>

            {/* ===== VEHICLE COMPARISON TABLE — SEO content section ===== */}
            <section
                className="max-w-7xl mx-auto px-4 sm:px-6 pb-24"
                aria-labelledby="vehicle-table-title"
                data-testid="vehicle-table-section"
            >
                <div className="text-center max-w-3xl mx-auto mb-10">
                    <div className="hz-pill">{t("home.ctaCabsTitle")} · {vehicleRows.length}</div>
                    <h2
                        id="vehicle-table-title"
                        className="font-hz-display text-3xl md:text-5xl tracking-tight mt-4"
                    >
                        {t("vehicleTable.title")}
                    </h2>
                    <p className="text-[var(--ink-soft)] mt-3 text-base">
                        {t("vehicleTable.sub")}
                    </p>
                </div>
                <div className="hz-card overflow-hidden bg-white border border-[var(--border)]">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-[var(--bg-surface)] text-[var(--ink-soft)]">
                                <tr>
                                    <th className="text-left px-5 py-4 font-semibold uppercase tracking-wider text-xs">
                                        {t("vehicleTable.vehicle")}
                                    </th>
                                    <th className="text-left px-5 py-4 font-semibold uppercase tracking-wider text-xs">
                                        {t("vehicleTable.seats")}
                                    </th>
                                    <th className="text-left px-5 py-4 font-semibold uppercase tracking-wider text-xs hidden sm:table-cell">
                                        {t("vehicleTable.luggage")}
                                    </th>
                                    <th className="text-left px-5 py-4 font-semibold uppercase tracking-wider text-xs">
                                        {t("vehicleTable.ratePerDay")}
                                    </th>
                                    <th className="text-left px-5 py-4 font-semibold uppercase tracking-wider text-xs hidden md:table-cell">
                                        {t("vehicleTable.bestFor")}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {vehicleRows.map((row, i) => (
                                    <tr
                                        key={i}
                                        className={`border-t border-[var(--border)] ${
                                            i % 2 === 0 ? "bg-white" : "bg-[var(--bg-surface)]"
                                        }`}
                                        data-testid={`vehicle-row-${i}`}
                                    >
                                        <td className="px-5 py-4 font-semibold text-[var(--ink)]">{row.v}</td>
                                        <td className="px-5 py-4">{row.s}</td>
                                        <td className="px-5 py-4 hidden sm:table-cell">{row.l}</td>
                                        <td className="px-5 py-4 font-hz-display text-base text-[var(--brand)]">
                                            {row.p}
                                        </td>
                                        <td className="px-5 py-4 hidden md:table-cell text-[var(--ink-soft)]">
                                            {row.b}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className="text-center mt-6">
                    <Link
                        to="/cab-booking"
                        className="hz-btn-primary"
                        data-testid="vehicle-table-cta"
                    >
                        {t("home.ctaCabsTitle")} <ArrowUpRight className="w-4 h-4" />
                    </Link>
                </div>
            </section>

            {/* DESTINATIONS — Popular destinations bento grid (dynamic from admin) */}
            <section className="max-w-7xl mx-auto px-6 pb-24" data-testid="preview-destinations">
                <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
                    <div>
                        <div className="hz-pill">{t("home.destinationsTitle")} · {popularDestinations.length}</div>
                        <h2 className="font-hz-display text-4xl md:text-5xl tracking-tight mt-4">
                            {t("home.destinationsTitle")}.
                        </h2>
                    </div>
                    <Link to="/destinations" className="hz-link text-sm" data-testid="popular-view-all">
                        {t("common.viewAll")}
                    </Link>
                </div>
                {popularDestinations.length === 0 ? (
                    <div className="text-center text-[var(--ink-soft)] py-16 border border-dashed border-[var(--border)] rounded-2xl">
                        Popular destinations are being prepared.
                    </div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 auto-rows-[180px] gap-4">
                        {popularDestinations.map((p) => {
                            const span =
                                p.cardSize === "large"
                                    ? "md:col-span-2 md:row-span-2"
                                    : p.cardSize === "medium"
                                    ? "md:col-span-2"
                                    : "";
                            return (
                                <Link
                                    key={p.id}
                                    to={p.linkUrl || "/destinations"}
                                    className={`hz-card relative overflow-hidden group ${span}`}
                                    data-testid={`popular-${p.id}`}
                                >
                                    <SmartImage
                                        src={p.imageUrl}
                                        alt={p.imageAlt || p.title}
                                        className="absolute inset-0 w-full h-full transition duration-700 group-hover:scale-110"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink)]/85 via-[var(--ink)]/20 to-transparent" />
                                    <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                                        {p.subtitle && (
                                            <div className="text-[10px] uppercase tracking-widest opacity-80">
                                                {p.subtitle}
                                            </div>
                                        )}
                                        <div className="font-hz-display text-2xl md:text-3xl mt-1 tracking-tight">
                                            {p.title}
                                        </div>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                )}
            </section>

            {/* MARQUEE OF BENEFITS */}
            <section className="border-y border-[var(--border)] overflow-hidden bg-[var(--bg-surface)]">
                <div className="flex hz-marquee-track whitespace-nowrap">
                    {[...Array(2)].map((_, k) => (
                        <div key={k} className="flex items-center gap-20 py-6 pr-20">
                            {[
                                t("home.trustSafe"),
                                t("home.trustRates"),
                                t("home.trustSupport"),
                                t("home.trustCertified"),
                                t("transfers.fixedPrice"),
                            ].map((tt) => (
                                <div key={`${k}-${tt}`} className="flex items-center gap-3 font-hz-display text-2xl text-[var(--ink)]">
                                    <span className="w-1.5 h-1.5 rounded-full bg-[var(--brand)]" />
                                    {tt}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </section>

            {/* FEATURED PACKAGES */}
            <section className="max-w-7xl mx-auto px-6 py-24" data-testid="preview-featured">
                <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
                    <div>
                        <div className="hz-pill">{t("home.bestselling")} · {featured.length}</div>
                        <h2 className="font-hz-display text-4xl md:text-5xl tracking-tight mt-4 max-w-xl leading-tight">
                            {t("home.bestselling")}.
                        </h2>
                    </div>
                    <Link to="/tour-packages" className="hz-btn-ghost text-sm hidden md:inline-flex">
                        {t("common.viewAll")} <ArrowUpRight className="w-4 h-4" />
                    </Link>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {featured.slice(0, 3).map((p, i) => (
                        <Link
                            to={`/tour-packages/${p.id}`}
                            key={p.id}
                            className="hz-card group overflow-hidden flex flex-col"
                            data-testid={`preview-pkg-${p.id}`}
                            style={{ animationDelay: `${i * 120}ms` }}
                        >
                            <div className="relative aspect-[4/3] overflow-hidden bg-[var(--bg-surface)]">
                                <img src={p.image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition duration-700" loading="lazy" />
                                <div className="absolute top-4 left-4 flex gap-2">
                                    <span className="hz-pill" style={{ background: "rgba(255,255,255,0.9)" }}>
                                        <MapPin className="w-3 h-3" /> {p.startingCity}
                                    </span>
                                    <span className="hz-pill" style={{ background: "var(--ink)", color: "#fff", border: "none" }}>
                                        {p.days}D
                                    </span>
                                </div>
                            </div>
                            <div className="p-6 flex-1 flex flex-col">
                                <h3 className="font-hz-display text-xl leading-tight">{p.route}</h3>
                                {p.highlight && (
                                    <div className="text-xs text-[var(--brand)] font-medium mt-1.5">{p.highlight}</div>
                                )}
                                <div className="mt-auto pt-6 flex items-end justify-between">
                                    <div>
                                        <div className="text-[10px] text-[var(--ink-softer)] uppercase tracking-widest">{t("common.from")}</div>
                                        <div className="font-hz-display text-3xl leading-none mt-1">
                                            {formatCurrency(p.sedanPrice ?? p.price)}
                                        </div>
                                    </div>
                                    <ArrowUpRight className="w-5 h-5 text-[var(--ink-soft)] group-hover:text-[var(--brand)] group-hover:rotate-45 transition" />
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            {/* REVIEWS CAROUSEL — Google or manual fallback */}
            <ReviewsCarousel />

            {/* TRUST GRID */}
            <section className="max-w-7xl mx-auto px-6 py-24">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                    {[
                        { big: "12", label: t("home.ctaCabsTitle") },
                        { big: "25+", label: t("home.ctaPackagesTitle") },
                        { big: "2.4k", label: t("home.testimonialsSub").split(" ").slice(1).join(" ").slice(0, 30) },
                        { big: "24/7", label: t("home.trustSupport") },
                    ].map((s) => (
                        <div key={s.label} className="hz-card p-8">
                            <div className="font-hz-display text-5xl md:text-6xl tracking-tight leading-none">
                                {s.big}
                            </div>
                            <div className="text-sm text-[var(--ink-soft)] mt-3">{s.label}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* FAQ — visible accordion (uses same 5 Q&As that drive FAQPage JSON-LD) */}
            <FAQ
                title="Kerala travel — frequently asked questions"
                intro="Quick answers about cab pricing, airport pickup, language support and more."
                items={HOMEPAGE_FAQS.map((it) => ({ q: it.q, a: it.a }))}
                idPrefix="home-faq"
            />

            {/* FINAL CTA */}
            <section className="max-w-7xl mx-auto px-6 pb-12">
                <div className="hz-card !bg-[var(--ink)] text-white p-10 md:p-16 rounded-[32px] grid grid-cols-1 md:grid-cols-12 gap-8 items-center hz-noise border-transparent">
                    <div className="md:col-span-8">
                        <div className="hz-chip-brand" style={{ background: "rgba(255,255,255,0.15)", color: "#fff" }}>
                            <ShieldCheck className="w-3 h-3" /> {t("booking.payDirectTitle")}
                        </div>
                        <h2 className="font-hz-display text-4xl md:text-6xl tracking-tight leading-[0.95] mt-6">
                            {t("home.conciergeTitle")}<br />
                            <span className="text-[#A5B4FC]">{t("home.conciergePromise")}</span>
                        </h2>
                        <p className="text-base md:text-lg text-white/75 mt-6 leading-relaxed max-w-2xl">
                            {t("home.conciergeSub")}
                        </p>
                    </div>
                    <div className="md:col-span-4 flex flex-col gap-3">
                        <Link to="/cab-booking" className="hz-btn-primary" style={{ background: "#fff", color: "var(--ink)" }}>
                            {t("services.cabBookingTitle")} <ArrowUpRight className="w-4 h-4" />
                        </Link>
                        <a href="tel:+918075814740" className="hz-btn-ghost text-white border-white/40 hover:bg-white hover:text-[var(--ink)]">
                            <Phone className="w-4 h-4" /> +91 80-75814740
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default PreviewHome;
